import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import CounterBadge from "@/components/ui/counter-badge";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import { SingleSelect } from "@/components/controls/SingleSelect";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";

const TRANSLATION_KEY = "settings.contracts.onboardingStudio.deleteStageModal";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDelete: (targetStageId: string | null) => void;
  stage: OnboardingStage | null;
  otherStages: OnboardingStage[];
  isPending?: boolean;
}

const DeleteOnboardingStageModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  handleDelete,
  stage,
  otherStages,
  isPending = false
}) => {
  const { t } = useTranslation();
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [targetStageId, setTargetStageId] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const isLastStage = otherStages.length === 0;
  const stageName = stage?.name ?? "";
  const contractsCount = stage?.contractsCount ?? 0;
  const needsTargetStage = !isLastStage && contractsCount > 0;

  const targetOptions = useMemo(
    () =>
      otherStages.map((stage) => ({
        id: stage.id,
        displayName: stage.name,
        name: stage.name
      })),
    [otherStages]
  );

  useEffect(() => {
    if (!isOpen) {
      setIsCheckboxChecked(false);
      setTargetStageId(null);
      setShowError(false);
    }
  }, [isOpen]);

  const canSubmit =
    isCheckboxChecked && (!needsTargetStage || targetStageId !== null);

  const onSubmit = () => {
    if (!canSubmit) {
      setShowError(true);
      return;
    }
    handleDelete(needsTargetStage ? targetStageId : null);
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem] [&_[data-test='close-modal']]:w-8 [&_[data-test='close-modal']]:h-8 [&_[data-test='close-modal']]:scale-100"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t(`${TRANSLATION_KEY}.title`, { stageName })}
        </Text>
      }
      content={
        <div className="flex flex-col gap-6 h-fit w-[35rem] p-4">
          <Text as="div">
            {t(`${TRANSLATION_KEY}.description`, { stageName })}
          </Text>

          <div className="flex flex-col gap-1">
            <div className="flex gap-[6px] items-center flex-wrap">
              <Text>{t(`${TRANSLATION_KEY}.contractsLine.prefix`)}</Text>
              <CounterBadge count={contractsCount} />
              <Text>{t(`${TRANSLATION_KEY}.contractsLine.suffix`)}</Text>
            </div>
            {needsTargetStage ? (
              <Text>{t(`${TRANSLATION_KEY}.chooseTargetMessage`)}</Text>
            ) : isLastStage ? (
              <Text>{t(`${TRANSLATION_KEY}.lastStageMessage`)}</Text>
            ) : null}
          </div>

          {needsTargetStage && (
            <div className="flex flex-col gap-2">
              <Text variant="smBodySecondary">
                {t(`${TRANSLATION_KEY}.targetStageLabel`)}
              </Text>
              <SingleSelect
                key={isOpen ? "open" : "closed"}
                fieldName="targetStage"
                variant="form"
                options={targetOptions}
                selectedValue={targetStageId ?? ""}
                onSelection={(value) => {
                  setTargetStageId(String(value));
                  if (showError) setShowError(false);
                }}
                placeholder={t(`${TRANSLATION_KEY}.targetStagePlaceholder`)}
                clearable={false}
                hasError={showError && targetStageId === null}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <SingleCheckbox
              id="permanentlyDeleteOnboardingStage"
              label={t(`${TRANSLATION_KEY}.checkbox`)}
              isChecked={isCheckboxChecked}
              onCheckedChange={(checked: boolean) => {
                setIsCheckboxChecked(checked);
                if (checked && showError) setShowError(false);
              }}
            />
            {showError && (
              <Text variant="smBody" className="text-error ml-2">
                {!isCheckboxChecked && needsTargetStage && targetStageId === null
                  ? t(`${TRANSLATION_KEY}.validationErrorBoth`)
                  : !isCheckboxChecked
                    ? t(`${TRANSLATION_KEY}.validationErrorCheckbox`)
                    : t(`${TRANSLATION_KEY}.validationErrorTarget`)}
              </Text>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            data-test="cancel"
            variant="secondary"
            onClick={() => setIsOpen(false)}
            disabled={isPending}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="delete"
            destructive
            onClick={onSubmit}
            disabled={isPending}
          >
            {t(`${TRANSLATION_KEY}.deleteStage`, { stageName })}
          </Button>
        </div>
      }
      hideDivider
    />
  );
};

export default DeleteOnboardingStageModal;
